import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="cart-empty">
        <ShoppingBag size={48} className="mx-auto text-muted-foreground/50" strokeWidth={1} />
        <h1 className="mt-6 text-3xl text-primary font-medium">Your cart is empty</h1>
        <p className="mt-3 text-muted-foreground">Beautiful things are waiting to be discovered.</p>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide hover:gap-3 transition-all"
          data-testid="cart-shop-link"
        >
          Start shopping <ArrowRight size={18} />
        </Link>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="cart-page">
      <h1 className="text-4xl text-primary font-medium mb-10">Shopping Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 divide-y divide-border border-y border-border">
          {items.map((item, idx) => (
            <div
              key={`${item.product_id}-${item.variant || "d"}-${idx}`}
              className="flex gap-4 sm:gap-6 py-6"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`} className="w-24 sm:w-28 aspect-[4/5] bg-muted overflow-hidden shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-4">
                  <div>
                    <Link to={`/product/${item.product_id}`} className="font-display text-lg text-foreground hover:text-primary">
                      {item.name}
                    </Link>
                    {item.variant && <p className="text-sm text-muted-foreground mt-0.5">{item.variant}</p>}
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id, item.variant)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`cart-remove-${item.product_id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center border border-border">
                    <button onClick={() => updateQty(item.product_id, item.variant, item.qty - 1)} className="px-3 py-2 hover:bg-muted" data-testid={`cart-minus-${item.product_id}`}>
                      <Minus size={14} />
                    </button>
                    <span className="px-4 text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => updateQty(item.product_id, item.variant, item.qty + 1)} className="px-3 py-2 hover:bg-muted" data-testid={`cart-plus-${item.product_id}`}>
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="font-semibold text-primary">{inr(item.price * item.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-secondary/50 border border-border p-6 lg:sticky lg:top-28">
            <h2 className="font-display text-xl text-primary mb-5">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold">{inr(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-primary font-semibold">Free</span>
              </div>
            </div>
            <div className="border-t border-border mt-4 pt-4 flex justify-between text-base">
              <span className="font-semibold">Total</span>
              <span className="font-semibold text-primary" data-testid="cart-total">{inr(subtotal)}</span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-6 w-full bg-primary text-primary-foreground py-4 text-sm tracking-wide hover:opacity-95 transition-opacity"
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </button>
            <Link to="/shop" className="mt-3 block text-center text-sm text-muted-foreground hover:text-primary">
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
